/**
 * @file Public landing page — pages/Landing/EventsPage.tsx.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./LandingPage.css";
import "./EventsPage.css";
import axiosClient from "../../api/axiosClient";
import { resolveUploadAssetUrl } from "../../utils/resolveUploadAssetUrl";
import { eventService, type PublicEvent } from "../../services/eventService";

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await eventService.listActive();
        const list = res.data?.data || [];
        if (!mounted) return;
        setEvents(list);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load events.");
        setEvents([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const sorted = useMemo(() => {
    const list = [...events];
    list.sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());
    return list;
  }, [events]);

  return (
    <div className="landing-root">
      <header className="landing-header flex justify-center items-center space-x-3 text-center py-4">
        <h1 className="landing-main-title text-4xl font-bold text-primary">
          ICoMEC
        </h1>
        <h2 className="landing-sub-title text-xl text-grayDark">
          Indian Construction Materials Embodied Carbon Data Base
        </h2>
      </header>

      <nav className="landing-nav">
        <Link to="/">Home</Link>
        <Link to="/join">Join</Link>
        <Link to="/impact">Impact</Link>
        <Link to="/research">Research and Analysis</Link>
        <Link to="/about">About</Link>
      </nav>

      <section className="landing-info" style={{ padding: "4rem 2rem", maxWidth: "900px", margin: "0 auto" }}>
        <h1 style={{ 
          fontSize: "2.5rem", 
          fontWeight: 500, 
          marginBottom: "1.5rem",
          fontFamily: "'Century Gothic', 'CenturyGothic', 'AppleGothic', sans-serif",
          color: "var(--text-color)"
        }}>
          Events
        </h1>

        {loading ? (
          <div className="events-state">Loading events…</div>
        ) : error ? (
          <div className="events-state events-state--error">{error}</div>
        ) : sorted.length === 0 ? (
          <div className="events-empty" role="status" aria-live="polite">
            <div className="events-empty__icon" aria-hidden="true">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="10" y="14" width="36" height="30" rx="10" stroke="currentColor" strokeWidth="2.5" />
                <path d="M10 22H46" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                <path d="M18 10V18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                <path d="M38 10V18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                <circle cx="22" cy="30" r="2.5" fill="currentColor" />
                <circle cx="28" cy="30" r="2.5" fill="currentColor" opacity="0.75" />
                <circle cx="34" cy="30" r="2.5" fill="currentColor" opacity="0.55" />
              </svg>
            </div>
            <h3 className="events-empty__title">No upcoming events</h3>
            <p className="events-empty__desc">
              We don’t have any active events published right now. Please check back soon, or explore the
              platform resources in the meantime.
            </p>
            <div className="events-empty__actions">
              <Link className="events-empty__btn" to="/resource-library">
                Go to Resource Library
              </Link>
              <Link className="events-empty__btn events-empty__btn--ghost" to="/join">
                Join the community
              </Link>
            </div>
          </div>
        ) : (
          <div className="events-grid">
            {sorted.map((ev) => {
              const imageUrl = ev.image
                ? resolveUploadAssetUrl(ev.image, axiosClient.defaults.baseURL)
                : "";
              return (
                <article key={ev.id} className="event-card">
                  {imageUrl && (
                    <div className="event-card__image">
                      <img
                        src={imageUrl}
                        alt={ev.title}
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = "none";
                        }}
                      />
                    </div>
                  )}
                  <div className="event-card__body">
                    <h3 className="event-card__title">{ev.title}</h3>
                    <div className="event-card__meta">
                      <div>
                        <span className="event-card__meta-label">Date</span>
                        <span className="event-card__meta-value">
                          {new Date(ev.eventDate).toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span className="event-card__meta-label">Place</span>
                        <span className="event-card__meta-value">{ev.place}</span>
                      </div>
                    </div>
                    <p className="event-card__content">{ev.content}</p>
                    {ev.registrationLink && (
                      <a
                        className="event-card__cta"
                        href={ev.registrationLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Registration Link
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <footer className="landing-footer">
        <div className="footer-main">
          <div className="footer-left">
            <h2>ICoMEC</h2>
            <p>
              Indian Construction Materials Embodied
              <br /> Carbon Data Base
            </p>
          </div>

          <div className="footer-links">
            <div>
              <h4>About Us</h4>
              <ul>
                <li><Link to="/who-we-are">Who We Are</Link></li>
                <li><Link to="/what-we-do">What We Do</Link></li>
                <li><Link to="/leadership">Leadership</Link></li>
                <li><Link to="/partners">Partners</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/glossary">Glossary</Link></li>
              </ul>
            </div>
            <div>
              <h4>Resources</h4>
              <ul>
                {/* <li><Link to="/resource-library">Resource Library</Link></li> */}
                <li><Link to="/events">Events</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      <div className="footer-bottom">
        <p>
          <Link to="/cookies-policy">Cookies Policy</Link> · <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/legal">Legal</Link> · <Link to="/terms-of-use">Terms of Use</Link>
          {/* · <Link to="/imprint">Imprint</Link> */}
        </p>
        <p>© 2026 CARBSE. All rights reserved.</p>
      </div>
    </div>
  );
};

/** React component — Events Page. */
export default EventsPage;

